import type { ReactNode } from 'react';
import { STATE_NAME } from '../constants/indianStates';
import { TerritoryChips } from './TerritoryChips';

/**
 * ONE WAY TO OFFER A RETAILER OR A FARMER IN A DROPDOWN.
 *
 * Every picker that lists customers used to build its own option label, and they had drifted into
 * three shapes: `Name · City` on the billing screens, `Name (CODE)` on complaints and feedback, and a
 * bare `Name` elsewhere. The same dealer therefore looked like a different record depending on which
 * screen you were standing on — and where only a name showed, there was no way at all to tell two
 * similarly-named shops apart, which is the entire job of a customer picker in a business with
 * hundreds of them.
 *
 * WHY THE PLACE, AND NOT THE CODE, LEADS. A rep choosing who they are standing in front of knows the
 * beat and the town, not the code. "Sangamner · Ahmednagar, Maharashtra" is answerable from memory;
 * "R-000871" has to be looked up. The code is kept, quietly at the end, for the desk that works by
 * document number — and both are searchable either way, because `title` carries the whole line.
 *
 * TWO FIELD NAMES, ONE IDEA. `stateCode` is stored as the GST code ("27"), which means something to
 * the tax engine and nothing to a reader, so it is resolved to the state's name. And a farmer's
 * `city` IS their village — the same column the farmer register labels "Village" — so no separate
 * village field exists to show, here or anywhere else.
 *
 * NO IDENTITY GLYPH. Each row used to lead with a shop (or person) icon. It told the reader nothing:
 * every list this feeds is single-kind — the complaint and feedback forms pick retailer-or-farmer with
 * a radio ABOVE the field, the visits page has one select per kind, and the billing pickers are
 * retailers only — so the same mark sat on every row. A mark that never varies is not identity, it is
 * texture, and here it was texture indenting the one thing being scanned.
 *
 * THE LEVEL GLYPHS ON THE PLACE ARE NOT THAT. A map, a building and a pin vary WITHIN one row: they
 * are what says which chip is the state and which the beat, and without them three coloured capsules
 * need a legend the dropdown has no room for. The place is now drawn by the app's one
 * {@link TerritoryChips}, so the dealer you pick here wears the same address you will see on the row
 * you get — previously the picker said "Pune, Pune, Maharashtra" in grey and the register said
 * something else entirely.
 */
export interface CustomerLike {
  id: string | number;
  code?: string | null;
  /** Farmers have no dealer code, so their number is what tells two Ramesh Patils apart. */
  mobile?: string | null;
  companyName?: string | null;
  fullName?: string | null;
  name?: string | null;
  city?: string | null;
  stateCode?: string | null;
  territoryName?: string | null;
  /** The territory NODE. Preferred over `territoryName`: it draws the whole State → District → Beat
   *  chain, where the name alone is one word with no level attached to it. */
  territoryId?: string | number | null;
}

export type CustomerKind = 'RETAILER' | 'FARMER';

/** The place, in the order a person would say it: beat, town, state. Empty when nothing is on file. */
export function customerPlace(c: CustomerLike): string {
  const state = c.stateCode ? (STATE_NAME.get(String(c.stateCode)) ?? String(c.stateCode)) : null;
  return [c.territoryName, c.city, state].filter(Boolean).join(', ');
}

export const customerName = (c: CustomerLike): string =>
  String(c.companyName ?? c.fullName ?? c.name ?? '').trim() || `#${c.id}`;

/**
 * Spread onto any `<Select>` fed by {@link customerOption}.
 *
 * `optionLabelProp` keeps the COLLAPSED control on one line: the dropdown row is two lines by design,
 * and rendering that block inside a 36px control would push the field out of the form. `title` is
 * both what the control then shows and what search reads, so the two can never disagree.
 */
export const CUSTOMER_SELECT_PROPS = {
  showSearch: true,
  optionFilterProp: 'title',
  optionLabelProp: 'title',
} as const;

/**
 * A dropdown option carrying the customer's identity AND where they are.
 *
 * `showMobile` is for the one screen where the number is the point rather than clutter: merging two
 * records of the same person, where "which of these is the live one" is answered by the handset.
 *
 * `kind` no longer changes what is drawn (see the glyph note above). It is kept because every caller
 * knows which list it is building and saying so keeps the call sites readable — and because the day a
 * picker genuinely mixes retailers and farmers, the distinction has to be made here, not invented.
 */
export function customerOption(
  c: CustomerLike,
  kind: CustomerKind = 'RETAILER',
  o: { showMobile?: boolean } = {},
): { value: string; title: string; label: ReactNode } {
  void kind;
  const name = customerName(c);
  const place = customerPlace(c);
  const ident = [c.code || null, (o.showMobile || !c.code) ? (c.mobile || null) : null]
    .filter(Boolean).join(' · ');
  return {
    value: String(c.id),
    title: [name, place, ident].filter(Boolean).join(' · '),
    label: (
      <span className="block min-w-0">
        {/* IDENTITY ON ONE LINE, PLACE ON THE NEXT. The number — a dealer code, or the handset that
            tells two Ramesh Patils apart — belongs with the name it identifies, not mixed into the
            address underneath it. `title` above still carries name, place and number as plain text,
            so search reads all three whatever this renders. */}
        <span className="flex min-w-0 items-baseline gap-1.5">
          <span className="truncate text-content">{name}</span>
          {ident ? <span className="shrink-0 font-mono type-caption text-subtle">{ident}</span> : null}
        </span>
        <span className="mt-0.5 flex min-w-0 items-center">
          <TerritoryChips territoryId={c.territoryId} fallbackName={c.territoryName} city={c.city} hideWhenEmpty />
        </span>
      </span>
    ),
  };
}
